import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Container,
  Typography,
  IconButton,
  Box,
  InputBase,
  alpha,
  Badge,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  useTheme,
  Button,
  Paper,
  useMediaQuery,
  Popper,
  Grow,
  ClickAwayListener,
  MenuList,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  SwipeableDrawer,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  Bell,
  Settings,
  HelpCircle,
  User,
  LogOut,
  Mail,
  Sun,
  Globe,
  LogIn,
  X as CloseIcon,
  ChevronRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import lngs from "../../lngs";
import { useEffect } from "react";

export default function MenuAppBar() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.resolvedLanguage;
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [scrolled, setScrolled] = useState(false);
  // State for various menus
  const [languageAnchorEl, setLanguageAnchorEl] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [user, setUser] = useState(
    JSON.parse(localStorage.getItem("profile")) || null
  );

  // Menu states
  const profileOpen = Boolean(profileAnchorEl);
  const notificationOpen = Boolean(notificationAnchorEl);
  const languageOpen = Boolean(languageAnchorEl);

  useEffect(() => {
    const rootElement = document.getElementById("root");

    const handleScroll = () => {
      const isScrolled = rootElement.scrollTop > 10;
      setScrolled(isScrolled);
    };

    rootElement.addEventListener("scroll", handleScroll);

    return () => {
      rootElement.removeEventListener("scroll", handleScroll);
    };
    // Only run once, on mount/unmount
  }, [scrolled]);

  // Handle menu openings and closings
  const handleProfileMenuOpen = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleLanguageMenuOpen = (event) => {
    setLanguageAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageAnchorEl(null);
  };

  const handleMobileDrawerOpen = () => {
    setMobileDrawerOpen(true);
  };

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  // Navigation and actions
  const openLink = (link) => {
    history.push(`${link}`);
    handleProfileMenuClose();
    handleMobileDrawerClose();
  };

  const logout = () => {
    dispatch({ type: "LOGOUT" });
    history.push(`/${currentLang}/login`);
    setUser(null);
    handleProfileMenuClose();
    handleMobileDrawerClose();
  };

  const handleLanguageChange = (languageCode, event) => {
    event.preventDefault();
    const currentPath = location.pathname;
    const regex = /^\/[a-z]{2}(?=\/|$)/; // Matches two-letter language code at the start of the path
    const newPath = currentPath.replace(regex, `/${languageCode}`);
    history.replace(newPath);
    i18n.changeLanguage(languageCode);
    handleLanguageMenuClose();
    handleMobileDrawerClose();
  };

  // Menu items
  const MenuSource = [
    {
      title: t("Home"),
      path: `/${currentLang}/`,
      showInMobileMenu: true,
      showDesktopMenu: true,
      showBoth: false,
    },
    {
      title: t("Prices"),
      path: `/${currentLang}/Prices`,
      showInMobileMenu: true,
      showDesktopMenu: true,
      showBoth: false,
    },
    {
      title: t("contactus"),
      path: `/${currentLang}/contactus`,
      showInMobileMenu: true,
      showDesktopMenu: true,
      showBoth: false,
    },
    {
      title: t("About"),
      path: `/${currentLang}/about`,
      showInMobileMenu: false,
      showDesktopMenu: false,
      showBoth: false,
    },
    {
      title: t("Download"),
      path: `/${currentLang}/download`,
      showInMobileMenu: false,
      showDesktopMenu: true,
      showBoth: false,
    },
    {
      title: t("Help"),
      path: `/${currentLang}/help`,
      showInMobileMenu: false,
      showDesktopMenu: false,
      showBoth: false,
    },
    {
      title: t("SignUp"),
      path: `/${currentLang}/signup`,
      showInMobileMenu: !user,
      showDesktopMenu: !user,
      showBoth: false,
    },
  ];

  // Mobile drawer
  const renderMobileDrawer = (
    <SwipeableDrawer
      anchor="right"
      open={mobileDrawerOpen}
      onClose={handleMobileDrawerClose}
      onOpen={handleMobileDrawerOpen}
      PaperProps={{
        sx: {
          width: 280,
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
        },
      }}
      sx={{ zIndex: 1300 }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t("Menu")}
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleMobileDrawerClose}
          aria-label="close"
        >
          <CloseIcon size={20} />
        </IconButton>
      </Box>

      <List sx={{ pt: 1, pb: 2 }}>
        {MenuSource.filter((link) => link.showInMobileMenu === true).map(
          (link, index) => (
            <ListItem key={`drawer-menu-${index}`} disablePadding>
              <ListItemButton
                component={Link}
                to={link.path}
                onClick={handleMobileDrawerClose}
                sx={{
                  py: 1.5,
                  px: 3,
                  "&:hover": {
                    bgcolor: "rgba(25, 118, 210, 0.08)",
                  },
                }}
              >
                <ListItemText primary={link.title} />
                <ChevronRight size={16} />
              </ListItemButton>
            </ListItem>
          )
        )}

        {!user && (
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to={`/${currentLang}/login`}
              onClick={handleMobileDrawerClose}
              sx={{
                py: 1.5,
                px: 3,
                color: theme.palette.primary.main,
                fontWeight: 600,
                "&:hover": {
                  bgcolor: "rgba(25, 118, 210, 0.08)",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: "auto", mr: 2, color: "inherit" }}>
                <LogIn size={18} />
              </ListItemIcon>
              <ListItemText
                primary={t("Login")}
                primaryTypographyProps={{ fontWeight: 600 }}
              />
              <ChevronRight size={16} />
            </ListItemButton>
          </ListItem>
        )}
      </List>

      <Divider />

      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {t("Language")}
        </Typography>
        <List disablePadding>
          {Object.keys(lngs).map((lng) => (
            <ListItem key={lng} disablePadding>
              <ListItemButton
                onClick={(e) => handleLanguageChange(lng, e)}
                sx={{
                  py: 1,
                  px: 2,
                  borderRadius: 1,
                  mb: 0.5,
                  "&:hover": {
                    bgcolor: "rgba(25, 118, 210, 0.08)",
                  },
                }}
              >
                <ListItemText primary={lngs[lng].nativeName} />
                {currentLang === lng && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {user && (
        <>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {t("Account")}
            </Typography>
            <List disablePadding>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => openLink(`/${currentLang}/app/settings/home`)}
                  sx={{
                    py: 1,
                    px: 2,
                    borderRadius: 1,
                    mb: 0.5,
                    "&:hover": {
                      bgcolor: "rgba(25, 118, 210, 0.08)",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "auto", mr: 2 }}>
                    <User size={18} />
                  </ListItemIcon>
                  <ListItemText primary={t("Profile")} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => openLink(`/${currentLang}/app/settings/home`)}
                  sx={{
                    py: 1,
                    px: 2,
                    borderRadius: 1,
                    mb: 0.5,
                    "&:hover": {
                      bgcolor: "rgba(25, 118, 210, 0.08)",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "auto", mr: 2 }}>
                    <Settings size={18} />
                  </ListItemIcon>
                  <ListItemText primary={t("Settings")} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => openLink(`/${currentLang}/app/dashboard`)}
                  sx={{
                    py: 1,
                    px: 2,
                    borderRadius: 1,
                    mb: 0.5,
                    "&:hover": {
                      bgcolor: "rgba(25, 118, 210, 0.08)",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "auto", mr: 2 }}>
                    <Sun size={18} />
                  </ListItemIcon>
                  <ListItemText primary={t("Application")} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={logout}
                  sx={{
                    py: 1,
                    px: 2,
                    borderRadius: 1,
                    color: "error.main",
                    "&:hover": {
                      bgcolor: "rgba(211, 47, 47, 0.08)",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: "auto",
                      mr: 2,
                      color: "error.main",
                    }}
                  >
                    <LogOut size={18} />
                  </ListItemIcon>
                  <ListItemText primary={t("Logout")} />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
        </>
      )}

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ p: 3, borderTop: "1px solid", borderColor: "divider" }}>
        <Typography variant="caption" color="text.secondary">
          {new Date().getFullYear()} Quantum Billing
        </Typography>
      </Box>
    </SwipeableDrawer>
  );

  return (
    <Box sx={{ width: "100%", zIndex: 1100000, display: "contents" }}>
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{
          bgcolor: scrolled ? "rgba(255, 255, 255, 0.95)" : "transparent",
          backdropFilter: scrolled ? "blur(10px)" : "none",
          borderBottom: scrolled ? "1px solid" : "none",
          borderColor: "divider",
          transition: "all 0.3s ease",
          boxShadow: scrolled ? "0 4px 20px rgba(0, 0, 0, 0.05)" : "none",
        }}
      >
        <Container>
          <Toolbar
            disableGutters
            sx={{ height: scrolled ? 70 : 80, transition: "height 0.3s ease" }}
          >
            {/* Logo */}
            <Link
              to={`/${currentLang}/`}
              style={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
              }}
            >
              <img
                style={{ width: "100px", height: "50px", marginRight: "16px" }}
                src="/logo.svg"
                alt="quantum-invoice"
              />
            </Link>

            {/* Desktop Menu Items */}
            <Box
              sx={{ display: { xs: "none", md: "flex" }, alignItems: "center" }}
            >
              {MenuSource.filter((link) => link.showDesktopMenu === true).map(
                (link, index) => (
                  <MenuItem key={`desktop-menu-${index}`}>
                    <Link
                      to={link.path}
                      style={{
                        textDecoration: "none",
                        color: "#333",
                        fontWeight: 500,
                        padding: "6px 12px",
                        borderRadius: "4px",
                      }}
                    >
                      {link.title}
                    </Link>
                  </MenuItem>
                )
              )}
            </Box>

            {/* Items shown in both mobile and desktop */}
            {MenuSource.filter((link) => link.showBoth === true).map(
              (link, index) => (
                <MenuItem key={`both-menu-${index}`}>
                  <Link
                    to={link.path}
                    style={{
                      textDecoration: "none",
                      color: "#333",
                      fontWeight: 500,
                      padding: "6px 12px",
                      borderRadius: "4px",
                    }}
                  >
                    {link.title}
                  </Link>
                </MenuItem>
              )
            )}

            <Box sx={{ flexGrow: 1 }} />

            {/* Right side icons */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {/* Login Button for non-logged in users */}
              {!user && (
                <Button
                  component={Link}
                  to={`/${currentLang}/login`}
                  variant="contained"
                  color="primary"
                  startIcon={<LogIn size={18} />}
                  sx={{
                    mr: 2,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    boxShadow: "0 4px 14px rgba(25, 118, 210, 0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 20px rgba(25, 118, 210, 0.4)",
                    },
                    display: { xs: "none", sm: "flex" },
                  }}
                >
                  {t("Login")}
                </Button>
              )}

              {/* Notifications */}
              {user && (
                <>
                  <Tooltip title={t("Notifications")}>
                    <IconButton
                      color="inherit"
                      size="medium"
                      onClick={handleNotificationMenuOpen}
                      aria-controls={
                        notificationOpen ? "notification-menu" : undefined
                      }
                      aria-haspopup="true"
                      aria-expanded={notificationOpen ? "true" : undefined}
                    >
                      <Badge badgeContent={4} color="error">
                        <Bell size={20} />
                      </Badge>
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={t("Messages")}>
                    <IconButton color="inherit" size="medium" sx={{ ml: 1 }}>
                      <Badge badgeContent={2} color="error">
                        <Mail size={20} />
                      </Badge>
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={t("Settings")}>
                    <IconButton
                      color="inherit"
                      size="medium"
                      sx={{ ml: 1 }}
                      onClick={() =>
                        openLink(`/${currentLang}/app/settings/home`)
                      }
                    >
                      <Settings size={20} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={t("Help")}>
                    <IconButton
                      color="inherit"
                      size="medium"
                      sx={{ ml: 1 }}
                      onClick={() => openLink(`/${currentLang}/help`)}
                    >
                      <HelpCircle size={20} />
                    </IconButton>
                  </Tooltip>

                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ mx: 1.5, my: 1 }}
                  />
                </>
              )}

              {/* Language selector */}
              <Tooltip title={t("Language")}>
                <IconButton
                  aria-controls="language-menu"
                  aria-haspopup="true"
                  onClick={handleLanguageMenuOpen}
                  color="inherit"
                  size="medium"
                >
                  <Globe size={20} />
                </IconButton>
              </Tooltip>

              {/* Mobile menu button */}
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  alignItems: "center",
                }}
              >
                {/* Login Button for mobile */}
                {!user && (
                  <IconButton
                    component={Link}
                    to={`/${currentLang}/login`}
                    color="primary"
                    size="medium"
                    sx={{
                      mr: 1,
                      bgcolor: "rgba(25, 118, 210, 0.1)",
                      "&:hover": {
                        bgcolor: "rgba(25, 118, 210, 0.2)",
                      },
                    }}
                  >
                    <LogIn size={20} />
                  </IconButton>
                )}

                <IconButton
                  aria-label="show more"
                  aria-haspopup="true"
                  onClick={handleMobileDrawerOpen}
                  color="inherit"
                  edge="end"
                  size="medium"
                  sx={{ ml: 1 }}
                >
                  <MenuIcon size={20} />
                </IconButton>
              </Box>

              {/* User profile */}
              {user && (
                <Tooltip title={t("Account")}>
                  <IconButton
                    onClick={handleProfileMenuOpen}
                    size="small"
                    sx={{ ml: 1 }}
                    aria-controls={profileOpen ? "account-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={profileOpen ? "true" : undefined}
                  >
                    <Avatar sx={{ width: 32, height: 32, bgcolor: "#1976D2" }}>
                      {user?.result?.name?.charAt(0)}
                    </Avatar>
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileAnchorEl}
        id="account-menu"
        open={profileOpen}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.1))",
            mt: 1.5,
            width: 220,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {user && (
          <>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {user?.result?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.result?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              sx={{ py: 1.5 }}
              onClick={() => openLink(`/${currentLang}/app/settings/home`)}
            >
              <ListItemIcon>
                <User size={18} />
              </ListItemIcon>
              {t("Profile")}
            </MenuItem>
            <MenuItem
              sx={{ py: 1.5 }}
              onClick={() => openLink(`/${currentLang}/app/settings/home`)}
            >
              <ListItemIcon>
                <Settings size={18} />
              </ListItemIcon>
              {t("Settings")}
            </MenuItem>
            <MenuItem
              sx={{ py: 1.5 }}
              onClick={() => openLink(`/${currentLang}/app/dashboard`)}
            >
              <ListItemIcon>
                <Sun size={18} />
              </ListItemIcon>
              {t("Application")}
            </MenuItem>
            <Divider />
            <MenuItem sx={{ py: 1.5, color: "error.main" }} onClick={logout}>
              <ListItemIcon>
                <LogOut size={18} color={theme.palette.error.main} />
              </ListItemIcon>
              {t("Logout")}
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchorEl}
        id="notification-menu"
        open={notificationOpen}
        onClose={handleNotificationMenuClose}
        onClick={handleNotificationMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.1))",
            mt: 1.5,
            width: 320,
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            {t("Notifications")}
          </Typography>
          <Typography
            variant="caption"
            color="primary"
            sx={{ cursor: "pointer", fontWeight: 500 }}
          >
            {t("Mark all as read")}
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ maxHeight: 320, overflow: "auto" }}>
          <MenuItem sx={{ py: 1.5, px: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body2" fontWeight={500}>
                {t("New order received")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("Order #38294 from Customer XYZ needs processing")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                10 {t("minutes ago")}
              </Typography>
            </Box>
          </MenuItem>
          <Divider />
          <MenuItem sx={{ py: 1.5, px: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body2" fontWeight={500}>
                {t("Low inventory alert")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("Product SKU-12345 is below minimum stock level")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                1 {t("hour ago")}
              </Typography>
            </Box>
          </MenuItem>
          <Divider />
          <MenuItem sx={{ py: 1.5, px: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body2" fontWeight={500}>
                {t("Payment received")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("Invoice #INV-5678 has been paid by Customer ABC")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                2 {t("hours ago")}
              </Typography>
            </Box>
          </MenuItem>
        </Box>
        <Divider />
        <Box sx={{ p: 1.5, textAlign: "center" }}>
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: "pointer", fontWeight: 500 }}
          >
            {t("View all notifications")}
          </Typography>
        </Box>
      </Menu>

      {/* Language Menu */}
      <Menu
        id="language-menu"
        anchorEl={languageAnchorEl}
        keepMounted
        open={languageOpen}
        onClose={handleLanguageMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.1))",
            mt: 1.5,
            width: 180,
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {Object.keys(lngs).map((lng) => (
          <MenuItem
            key={lng}
            onClick={handleLanguageMenuClose}
            sx={{ py: 1.5 }}
          >
            <a
              href={`/${lng}`}
              style={{
                textDecoration: "none",
                color: "inherit",
                width: "100%",
              }}
              onClick={(e) => handleLanguageChange(lng, e)}
            >
              {lngs[lng].nativeName}
            </a>
          </MenuItem>
        ))}
      </Menu>

      {/* Mobile drawer */}
      {renderMobileDrawer}
    </Box>
  );
}
